import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from '../../redux/store';
import { useParams } from 'react-router-dom';
import Page from '../../components/Page';
import useSettings from '../../hooks/useSettings';
import {
    Container,
    Grid,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    Divider,
    Paper,
    Box,
    Chip,
} from '@mui/material';
import Logo from '../../components/Logo';
import moment from 'moment';
import { fCurrency } from '../../utils/formatNumber';
import { getPurchaseNoteByToken } from '../../redux/slices/purchaseRedux/purchaseNotesRedux';
import { handleNumber, safeJSONParse, utcMoment } from '../../utils/common';
import { COMPANY_DATA_FOR_PDF } from '../../config';
import { Alert } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';


export default function PurchaseNotePublicView() {
    const { token } = useParams();
    const dispatch = useDispatch();
    const { themeStretch } = useSettings();
    const { publicNote } = useSelector((state) => state.purchaseNotes);

    useEffect(() => {
        if (token) dispatch(getPurchaseNoteByToken(token));
    }, [token, dispatch]);

    const getItemsForInvoice = (rowData) => {
        const parsed = safeJSONParse(rowData) || [];
        return parsed.map((value, index) => {
            const jsonData = safeJSONParse(value?.jsonData);
            const unitPrice =
                (jsonData?.price / jsonData?.purchaseUnitQty) *
                jsonData?.storeUnitQty;

            return {
                id: index,
                title: jsonData?.FoodName,
                description: `Price verified on ${jsonData?.formatted_dateTime}`,
                quantity: jsonData?.purchaseUnitQty,
                storeUnitQty: `${jsonData?.storeUnitQty} ${jsonData?.unitSymbol}`,
                price: unitPrice,
            };
        });
    };

    const formattedNote = useMemo(() => {
        if (!publicNote) return null;

        return {
            invoiceNumber: `PN-${publicNote.id}`,
            createDate: utcMoment(publicNote.date).format('DD MMM YYYY'),
            status: publicNote.status,
            subTotalPrice: handleNumber(publicNote.totalamount),
            paidAmount: handleNumber(publicNote.paidamount),
            totalPrice:
                handleNumber(publicNote.totalamount) - handleNumber(publicNote.paidamount),
            invoiceTo: {
                name: COMPANY_DATA_FOR_PDF?.name,
                address: publicNote.locationAddress,
                phone: publicNote.locationContactNumber,
            },
            invoiceFrom: {
                name: publicNote.supplierName,
                address: publicNote.supplierAddress,
                phone: publicNote.supplierCompanyPhone,
            },
            items: getItemsForInvoice(
                publicNote.supplierorder_foodsupplierpricing
            ),
            sentAt: utcMoment(publicNote.sentAt).format('DD MMM YYYY, hh:mm A'),
            sentByUserName: publicNote.sentByUserName,
            supplierCompanyPhone: publicNote.supplierCompanyPhone
        };
    }, [publicNote]);

    if (!formattedNote) {
        return (
            <Page title="Purchase Note">
                <Container>
                    <Typography>Loading purchase order</Typography>
                </Container>
            </Page>
        );
    }

    const {
        items,
        invoiceNumber,
        createDate,
        invoiceFrom,
        invoiceTo,
        subTotalPrice,
        totalPrice,
        status,
        sentAt,
        sentByUserName,
        supplierCompanyPhone,
    } = formattedNote;

    return (
        <Page title={`Purchase Note ${invoiceNumber}`} >
            <Container maxWidth={'lg'}>
                <Paper variant="outlined" sx={{
                    p: 4, bgcolor: '#fff', boxShadow: '0px 8px 24px rgba(0,0,0,0.12)'}}>
                    {/* Header */}
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <Logo sx={{ width: 64, height: 64 }} />
                        </Grid>
                        <Grid item xs>
                            <Typography variant="h4" fontWeight={700}>
                                Purchase Order
                            </Typography>
                            <Typography color="text.secondary">
                                {invoiceNumber}
                            </Typography>
                        </Grid>
                        <Grid item>
                            {/* <Chip
                                label={status}
                                color="primary"
                                variant="outlined"
                            /> */}
                        </Grid>
                    </Grid>


                    {sentAt && sentByUserName && (
                        <>
                            <Divider sx={{ my: 3 }} />

                            <Alert
                                severity="info"
                                icon={<InfoOutlined />}
                                sx={{
                                    alignItems: 'center',
                                    fontSize: 14,
                                }}
                            >
                                This purchase order was sent to the supplier
                                {supplierCompanyPhone && (
                                    <> (<strong>{supplierCompanyPhone}</strong>)</>
                                )}
                                {' '}on{' '}
                                <strong>{sentAt}</strong>
                                {' '}by{' '}
                                <strong>{sentByUserName}</strong>.
                            </Alert>
                        </>
                    )}

                    <Divider sx={{ my: 3 }} />

                    {/* Parties */}
                    <Grid container spacing={4}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="overline">Supplier</Typography>
                            <Typography fontWeight={600}>{invoiceFrom.name}</Typography>
                            <Typography variant="body2">{invoiceFrom.address}</Typography>
                            <Typography variant="body2">{invoiceFrom.phone}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="overline">Deliver To</Typography>
                            <Typography fontWeight={600}>{invoiceTo.name}</Typography>
                            <Typography variant="body2">{invoiceTo.address}</Typography>
                            <Typography variant="body2">{invoiceTo.phone}</Typography>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Meta */}
                    <Typography variant="body2">
                        <strong>Date:</strong> {createDate}
                    </Typography>

                    <Divider sx={{ my: 3 }} />

                    {/* Items Table */}
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Item</TableCell>
                                    <TableCell align="right">Qty</TableCell>
                                    <TableCell align="right">Unit Price</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item, idx) => (
                                    <TableRow key={item.id}>
                                        <TableCell>{idx + 1}</TableCell>
                                        <TableCell>
                                            <Typography fontWeight={600}>
                                                {item.title}
                                            </Typography>
                                            {/* <Typography variant="caption" display="block">
                                                {item.description}
                                            </Typography> */}
                                            <Typography variant="caption" color="text.secondary">
                                                Std Unit: {item.storeUnitQty}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">{item.quantity}</TableCell>
                                        <TableCell align="right">
                                            {fCurrency(item.price)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {fCurrency(item.price * item.quantity)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Totals */}
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Box sx={{ minWidth: 280 }}>
                            <Grid container spacing={1}>
                                <Grid item xs={6}>
                                    <Typography>Subtotal</Typography>
                                </Grid>
                                <Grid item xs={6} textAlign="right">
                                    <Typography>{fCurrency(subTotalPrice)}</Typography>
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography fontWeight={700}>Total Due</Typography>
                                </Grid>
                                <Grid item xs={6} textAlign="right">
                                    <Typography fontWeight={700}>
                                        {fCurrency(totalPrice)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        align="center"
                        display="block"
                    >
                        This is a system-generated purchase note.
                    </Typography>
                </Paper>
            </Container>
        </Page>
    );
}
